///
/// EarthBrowser - Drawer Tab
///
/// Individual draggable tab in the bottom drawer
/// Created by Matt Giger on December 28, 2025
/// Copyright (c) 2025 Geodata Labs
///

import { cDatasetWindowMeta } from "@/components/windows/datasetWindowMeta"
import type { DatasetBrowserWindowState } from "@/redux/dialogSlice"
import { cn } from "@/utils/utils"
import { X } from "lucide-react"
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react"
import { createPortal } from "react-dom"

// Threshold for detecting drag-out (pixels from tab bar)
const UNDOCK_THRESHOLD = 60

export interface DrawerTabProps {
	window: DatasetBrowserWindowState
	isActive: boolean
	onClick: () => void
	onClose: () => void
	onUndock: (windowId: string, x: number, y: number) => void
	getWindowIcon?: (windowType?: string) => { icon: ReactNode; color: string } | null
	// Visibility toggle support
	isVisible?: boolean
	onToggleVisibility?: () => void
	// Optional: register the DOM node for reorder hit testing
	registerTabElement?: (windowId: string, el: HTMLDivElement | null) => void
	// Optional: allow parent to handle in-drawer reordering while dragging
	onReorderDragStart?: (windowId: string) => void
	onReorderDragMove?: (windowId: string, clientX: number, clientY: number, isUndocking: boolean) => void
	onReorderDragEnd?: (windowId: string, didUndock: boolean) => void
}

// Default icon getter, driven by the shared dataset window registry
function getDefaultWindowIcon(windowType?: string) {
	const meta = windowType ? cDatasetWindowMeta[windowType] : undefined
	if (!meta) return null
	return { icon: <meta.Icon className="w-3.5 h-3.5" />, color: meta.colorClass }
}

export function DrawerTab({
	window: windowState,
	isActive,
	onClick,
	onClose,
	onUndock,
	getWindowIcon = getDefaultWindowIcon,
	isVisible,
	onToggleVisibility,
	registerTabElement,
	onReorderDragStart,
	onReorderDragMove,
	onReorderDragEnd,
}: DrawerTabProps) {
	const tabRef = useRef<HTMLDivElement>(null)
	const [isDragging, setIsDragging] = useState(false)
	const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
	const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 })
	const [isUndocking, setIsUndocking] = useState(false)
	const dragStartRef = useRef({ x: 0, y: 0 })

	// Check if window can be undocked
	const canUndock = windowState.dockMode !== "dock-only"

	// Get icon for this window type
	const iconInfo = getWindowIcon(windowState.windowType)

	// Handle pointer down
	const handlePointerDown = useCallback((e: React.PointerEvent) => {
		// Don't start drag from close button
		if ((e.target as HTMLElement).closest("button")) return
		if (!canUndock) return

		e.preventDefault()
		e.stopPropagation()

		const rect = tabRef.current?.getBoundingClientRect()
		if (!rect) return

		if (e.pointerType === "touch") {
			(e.target as HTMLElement).setPointerCapture(e.pointerId)
		}

		setDragOffset({
			x: e.clientX - rect.left,
			y: e.clientY - rect.top,
		})
		dragStartRef.current = { x: e.clientX, y: e.clientY }
		setDragPosition({ x: e.clientX, y: e.clientY })
		setIsDragging(true)
		setIsUndocking(false)

		onReorderDragStart?.(windowState.id)
	}, [canUndock, onReorderDragStart, windowState.id])

	// Handle pointer move
	const handlePointerMove = useCallback((e: PointerEvent) => {
		if (!isDragging) return

		setDragPosition({ x: e.clientX, y: e.clientY })

		// Check if dragged far enough up to undock
		const deltaY = dragStartRef.current.y - e.clientY
		const nextIsUndocking = deltaY > UNDOCK_THRESHOLD
		setIsUndocking(nextIsUndocking)
		onReorderDragMove?.(windowState.id, e.clientX, e.clientY, nextIsUndocking)
	}, [isDragging, onReorderDragMove, windowState.id])

	// Handle pointer up
	const handlePointerUp = useCallback((e: PointerEvent) => {
		if (e.pointerType === "touch") {
			(e.target as HTMLElement)?.releasePointerCapture?.(e.pointerId)
		}

		if (isDragging) {
			if (isUndocking) {
				// Calculate window position from drag position
				const windowWidth = windowState.width || 600
				const windowHeight = windowState.height || 400
				const x = Math.max(0, Math.min(e.clientX - windowWidth / 2, window.innerWidth - windowWidth))
				const y = Math.max(0, Math.min(e.clientY - 20, window.innerHeight - windowHeight - 50))

				onUndock(windowState.id, x, y)
			} else {
				// It was just a click, select the tab
				const deltaX = Math.abs(e.clientX - dragStartRef.current.x)
				const deltaY = Math.abs(e.clientY - dragStartRef.current.y)
				if (deltaX < 5 && deltaY < 5) {
					onClick()
				}
			}
		}

		onReorderDragEnd?.(windowState.id, isUndocking)

		setIsDragging(false)
		setIsUndocking(false)
	}, [isDragging, isUndocking, windowState, onUndock, onClick, onReorderDragEnd])

	// Set up event listeners
	useEffect(() => {
		if (isDragging) {
			document.addEventListener("pointermove", handlePointerMove)
			document.addEventListener("pointerup", handlePointerUp)

			return () => {
				document.removeEventListener("pointermove", handlePointerMove)
				document.removeEventListener("pointerup", handlePointerUp)
			}
		}
	}, [isDragging, handlePointerMove, handlePointerUp])

	return (
		<>
			{/* Tab */}
			<div
				ref={(el) => {
					tabRef.current = el
					registerTabElement?.(windowState.id, el)
				}}
				data-drawer-tab-id={windowState.id}
				className={cn(
					"relative flex items-center gap-1.5 px-3 py-1.5 rounded-t-lg",
					"cursor-pointer select-none transition-all duration-200",
					"border border-b-0 border-transparent",
					isActive
						? "bg-slate-700/50 border-slate-500/50 text-white"
						: "bg-transparent text-white/60 hover:bg-slate-700/30 hover:text-white/80",
					isDragging && "opacity-50",
					canUndock && "cursor-grab",
					isDragging && canUndock && "cursor-grabbing"
				)}
				onPointerDown={handlePointerDown}
				onClick={(e) => {
					if (!isDragging) {
						e.stopPropagation()
						onClick()
					}
				}}
			>
				{/* Visibility toggle */}
				{onToggleVisibility !== undefined && (
					<button
						onClick={(e) => {
							e.stopPropagation()
							onToggleVisibility()
						}}
						className={cn(
							"flex-shrink-0 w-4 h-4 rounded flex items-center justify-center",
							"transition-all duration-200",
							"focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-cyan-500/50",
							isVisible
								? "bg-cyan-500/30 hover:bg-cyan-500/40"
								: "bg-white/10 hover:bg-white/20"
						)}
						aria-label={isVisible ? "Hide from map" : "Show on map"}
						title={isVisible ? "Hide from map" : "Show on map"}
					>
						<div className={cn(
							"w-1.5 h-1.5 rounded-full transition-all duration-200",
							isVisible ? "bg-cyan-400" : "bg-white/40"
						)} />
					</button>
				)}

				{/* Icon */}
				{iconInfo && (
					<div className={cn("flex-shrink-0", iconInfo.color)}>
						{iconInfo.icon}
					</div>
				)}

				{/* Title */}
				<span className="text-xs font-medium truncate max-w-[120px]">
					{windowState.title}
				</span>

				{/* Close button */}
				<button
					onClick={(e) => {
						e.stopPropagation()
						onClose()
					}}
					className={cn(
						"flex-shrink-0 p-0.5 rounded-full",
						"hover:bg-red-500/20 transition-all duration-200",
						"opacity-60 hover:opacity-100"
					)}
					aria-label="Close tab"
				>
					<X className="w-3 h-3" />
				</button>
			</div>

			{/* Drag ghost: portal to body so position:fixed uses the viewport (drawer uses transform) */}
			{isDragging &&
				createPortal(
					<div
						className={cn(
							"fixed pointer-events-none z-[9999]",
							"flex items-center gap-1.5 px-3 py-1.5 rounded-lg",
							"bg-slate-800/90 backdrop-blur-sm border border-slate-500/50",
							"shadow-xl",
							isUndocking && "ring-2 ring-cyan-400/50"
						)}
						style={{
							left: dragPosition.x - dragOffset.x,
							top: dragPosition.y - dragOffset.y,
						}}
					>
						{/* Invisible chrome so drag offset (from full tab rect) matches this row */}
						{onToggleVisibility !== undefined && (
							<div
								className={cn(
									"flex-shrink-0 w-4 h-4 rounded flex items-center justify-center",
									"opacity-0 pointer-events-none"
								)}
								aria-hidden
							>
								<div className="w-1.5 h-1.5 rounded-full" />
							</div>
						)}

						{/* Icon */}
						{iconInfo && (
							<div className={cn("flex-shrink-0", iconInfo.color)}>
								{iconInfo.icon}
							</div>
						)}

						{/* Title */}
						<span className="text-xs font-medium text-white truncate max-w-[120px]">
							{windowState.title}
						</span>

						{/* Undock indicator */}
						{isUndocking && (
							<span className="text-[10px] text-cyan-400 ml-1 whitespace-nowrap">
								Release to undock
							</span>
						)}

						<div
							className="flex-shrink-0 p-0.5 rounded-full opacity-0 pointer-events-none"
							aria-hidden
						>
							<X className="w-3 h-3" />
						</div>
					</div>,
					document.body
				)}
		</>
	)
}

